<div class="widget-shell">
  @if (isOpen()) {
    <section class="chat-panel">
      <header class="chat-header">
        <div class="chat-header-main">
          <div class="bot-avatar" aria-hidden="true">
            <ng-container *ngTemplateOutlet="ultreiaIcon" />
          </div>
          <div class="bot-meta">
            <h1>{{ botName }}</h1>
            <p class="connection-status" [class.is-online]="connectionState() === 'connected'">
              <span class="connection-status-dot" aria-hidden="true"></span>
              <span>{{ connectionLabel }}</span>
            </p>
          </div>
        </div>

        <div class="chat-header-actions">
          <button
            class="header-action-button reset-button"
            type="button"
            (click)="resetConversation()"
            [attr.aria-label]="uiText().resetChatLabel"
          >
            {{ uiText().resetChatLabel }}
          </button>

          <button
            class="header-action-button minimize-button"
            type="button"
            (click)="toggleWidget()"
            [attr.aria-label]="uiText().minimizeChatLabel"
          >
            <span aria-hidden="true">⌄</span>
          </button>
        </div>
      </header>

      <div class="chat-body" #chatBody>
        @if (errorMessage() && !isConversationBlocked()) {
          <div class="chat-alert">
            <p>{{ errorMessage() }}</p>
          </div>
        }

        @for (message of messages(); track trackByMessageId($index, message)) {
          <article
            #messageRow
            class="message-row"
            [class.message-row-user]="message.author === 'user'"
          >
            @if (message.author === 'bot') {
              <div class="message-avatar" aria-hidden="true">
                <ng-container *ngTemplateOutlet="ultreiaIcon" />
              </div>
            }

            <div class="message-bubble" [class.message-bubble-user]="message.author === 'user'">
              <div class="message-content">
                @for (
                  block of message.content | chatMessageFormat: hasStructuredSources(message);
                  track $index
                ) {
                  @switch (block.type) {
                    @case ('heading') {
                      <h2 class="message-heading">
                        <ng-container
                          *ngTemplateOutlet="inlineSegments; context: { $implicit: block.segments }"
                        />
                      </h2>
                    }
                    @case ('list') {
                      <ul class="message-list">
                        @for (item of block.items; track $index) {
                          <li>
                            <ng-container
                              *ngTemplateOutlet="inlineSegments; context: { $implicit: item }"
                            />
                          </li>
                        }
                      </ul>
                    }
                    @case ('ordered-list') {
                      <ol class="message-list">
                        @for (item of block.items; track $index) {
                          <li>
                            <ng-container
                              *ngTemplateOutlet="inlineSegments; context: { $implicit: item }"
                            />
                          </li>
                        }
                      </ol>
                    }
                    @case ('source-title') {
                      <p class="message-source-title">
                        <ng-container
                          *ngTemplateOutlet="inlineSegments; context: { $implicit: block.segments }"
                        />
                      </p>
                    }
                    @case ('source') {
                      <p class="message-source">
                        <ng-container
                          *ngTemplateOutlet="inlineSegments; context: { $implicit: block.segments }"
                        />
                      </p>
                    }
                    @case ('quote') {
                      <blockquote class="message-quote">
                        <ng-container
                          *ngTemplateOutlet="inlineSegments; context: { $implicit: block.segments }"
                        />
                      </blockquote>
                    }
                    @default {
                      <p class="message-paragraph">
                        <ng-container
                          *ngTemplateOutlet="inlineSegments; context: { $implicit: block.segments }"
                        />
                      </p>
                    }
                  }
                }
              </div>

              @if (message.author === 'bot' && message.sources?.length) {
                <details class="message-sources">
                  <summary>
                    <span class="source-chip-icons" aria-hidden="true">
                      @for (_source of getSourcePreviewSources(message); track $index) {
                        <span
                          class="source-chip-icon"
                          [class.source-chip-icon-pilgrim]="$index === 0"
                          [class.source-chip-icon-blue]="$index === 1"
                          [class.source-chip-icon-beige]="$index === 2"
                        ></span>
                      }
                    </span>
                    <span>{{ uiText().sourcesLabel }}</span>
                  </summary>

                  <ul class="message-sources-list">
                    @for (source of message.sources; track $index) {
                      <li>
                        <a
                          class="message-source-link"
                          [href]="source.url"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ source.label }}
                        </a>
                      </li>
                    }
                  </ul>
                </details>
              }

              <span class="message-time">{{ formatTime(message.createdAt) }}</span>
            </div>
          </article>
        }

        @if (isBotTyping() && !isConversationBlocked()) {
          <article class="message-row typing-row" role="status" aria-live="polite">
            <div class="message-avatar" aria-hidden="true">
              <ng-container *ngTemplateOutlet="ultreiaIcon" />
            </div>

            <div class="typing-bubble" [attr.aria-label]="uiText().typingLabel">
              <span class="typing-dots" aria-hidden="true">
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
              </span>
            </div>
          </article>
        }

        @if (isConversationBlocked() && errorMessage()) {
          <div class="chat-system-notice" role="status" aria-live="polite">
            <span class="chat-system-notice-label">{{ uiText().systemNoticeLabel }}</span>
            <p>{{ errorMessage() }}</p>
          </div>
        }
      </div>

      <footer class="chat-footer">
        <div class="message-input">
          <textarea
            #messageInput
            rows="1"
            [attr.maxlength]="messageMaxCharacters()"
            [ngModel]="draftMessage()"
            (ngModelChange)="updateDraftMessage($event)"
            (input)="resizeMessageInput()"
            (keydown.enter)="handleEnter($event)"
            [disabled]="isConversationBlocked()"
            [attr.aria-label]="uiText().inputAriaLabel"
            [placeholder]="
              isConversationBlocked()
                ? uiText().blockedInputPlaceholder
                : uiText().inputPlaceholder
            "
          ></textarea>

          <span class="character-counter">
            {{ draftMessageLength() }}/{{ messageMaxCharacters() }}
          </span>
        </div>

        <button
          type="button"
          class="send-button"
          (click)="sendMessage()"
          [disabled]="!canSendMessage()"
          [attr.aria-label]="uiText().sendMessageLabel"
        >
          ➤
        </button>
      </footer>
    </section>
  } @else {
    <button class="launcher-button" type="button" (click)="toggleWidget()" aria-label="Abrir chat">
      <ng-container *ngTemplateOutlet="ultreiaIcon" />
    </button>
  }
</div>

<ng-template #ultreiaIcon>
  <img class="ultreia-icon" src="ultreia-avatar.png" alt="" aria-hidden="true" />
</ng-template>

<ng-template #inlineSegments let-segments>
  @for (segment of segments; track $index) {
    @switch (segment.type) {
      @case ('strong') {
        <strong>{{ segment.text }}</strong>
      }
      @case ('emphasis') {
        <em>{{ segment.text }}</em>
      }
      @case ('code') {
        <code>{{ segment.text }}</code>
      }
      @case ('link') {
        <a class="message-link" [href]="segment.href" target="_blank" rel="noopener noreferrer">
          {{ segment.text }}
        </a>
      }
      @default {
        <ng-container>{{ segment.text }}</ng-container>
      }
    }
  }
</ng-template>
